"use client";

import React, { useState } from "react";
import { PhoneCall, Mail, MessageSquare, Copy, Check, Loader } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const inputClasses = "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
const textareaClasses = "flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";
function SupportForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/10 border border-primary/20 rounded-xl text-center space-y-4">
                <div className="mx-auto size-12 bg-primary/20 rounded-lg flex items-center justify-center mb-4">
                    <Check className="size-6 text-primary" />
                </div>
                <h3 className="text-xl font-semibold font-default text-foreground">Information Submitted</h3>
                <p className="text-muted-foreground">{successMessage}</p>
                <Button onClick={resetForm} variant="outline" className="mt-4 font-default">
                    Submit Again
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField control={control} name="name" rules={{
        required: "Please enter your name"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default">Your Name</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="e.g., Mr. Chen" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Please fill in your real name</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="phone" rules={{
        required: "Please enter your phone number"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default">Phone Number</FormLabel>
                            <FormControl>
                                <input type="tel" placeholder="+65 8888 8888" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">We will contact you via this number</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Please enter your email",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default">Email</FormLabel>
                            <FormControl>
                                <input type="email" placeholder="rickychen@stiholdinggroup.com" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Used for sending project materials or investment advice</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="company" rules={{
        required: "Please enter your company name"
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default">Company/Organization Name</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="Your Company Name" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">If you represent an institution, please specify</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="interest" rules={{
        required: "Please select your area of interest"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="font-default">Investment Interest Areas</FormLabel>
                            <FormControl>
                                <input type="text" placeholder="e.g., Private Equity, Luxury Goods, IT Internet" className={inputClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Please briefly describe the business sectors you are interested in</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="issue" rules={{
        required: "Please enter your message"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
                            <FormLabel className="font-default">Message Details</FormLabel>
                            <FormControl>
                                <textarea placeholder="Please describe your specific needs or cooperation intentions..." className={textareaClasses} {...field} />
                            </FormControl>
                            <p className="text-[0.8rem] text-muted-foreground">Providing more details helps us match you with a professional investment advisor</p>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="preferred_contact" rules={{
        required: "Please select your preferred contact method"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2 space-y-3">
                            <FormLabel className="font-default">Preferred Contact Method</FormLabel>
                            <FormControl>
                                <div className="flex gap-6">
                                    {["Phone", "Email"].map((option, i) => <label key={option} data-index={i} className="flex items-center space-x-2 cursor-pointer">
                                            <input type="radio" value={option} checked={field.value === option} onChange={field.onChange} className="size-4 border-primary text-primary focus:ring-primary bg-background" />
                                            <span className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 font-default">{option}</span>
                                        </label>)}
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            {submitError && <div className="mt-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <p className="text-destructive text-sm font-medium font-default">{submitError}</p>
                </div>}

            <div className="mt-8">
                <Button type="submit" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] bg-primary text-primary-foreground hover:bg-primary/90 font-default rounded-md">
                    {isSubmitting ? <>
                            <Loader className="animate-spin mr-2 size-5" />
                            Submitting...
                        </> : 'Submit Inquiry Now'}
                </Button>
            </div>
        </Form>;
}
export default function DirectSupport() {
  const [copiedPhone, setCopiedPhone] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const phone = "+65 8888 8888";
  const email = "rickychen@stiholdinggroup.com";
  const handleCopy = (text: string, type: 'phone' | 'email') => {
    navigator.clipboard.writeText(text);
    if (type === 'phone') {
      setCopiedPhone(true);
      setTimeout(() => setCopiedPhone(false), 2000);
    } else {
      setCopiedEmail(true);
      setTimeout(() => setCopiedEmail(false), 2000);
    }
  };
  return <section data-section-id="246984" id="contact-us" className="bg-background text-foreground py-24 px-4 md:px-8 lg:px-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#22000008_1px,transparent_1px),linear-gradient(to_bottom,#22000008_1px,transparent_1px)] bg-[size:32px_32px] pointer-events-none"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-primary/5 blur-[120px] rounded-lg pointer-events-none"></div>

            <div className="max-w-7xl mx-auto relative z-10">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <h2 className="text-3xl md:text-5xl font-bold font-default tracking-tight mb-4 text-primary">
                        Contact Us
                    </h2>
                    <p className="text-lg text-muted-foreground font-default">
                        Singapore STI Group is dedicated to providing comprehensive strategic support and capital empowerment through the dual engines of capital and innovation. Feel free to inquire at any time.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
                    <Card className="border-border shadow-lg shadow-primary/5 relative overflow-hidden group bg-card">
                        <div className="absolute inset-0 bg-gradient-to-br from-accent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                        <CardHeader className="text-center pb-2">
                            <div className="mx-auto bg-secondary p-4 rounded-xl mb-4">
                                <PhoneCall className="size-12 text-primary" />
                            </div>
                            <CardTitle className="text-2xl font-default">Headquarters</CardTitle>
                            <CardDescription className="text-base font-default text-primary">346C KING GEORGE'S AVENUE, SINGAPORE 208577</CardDescription>
                        </CardHeader>
                        <CardContent className="text-center pb-6">
                            
                        </CardContent>
                        <CardFooter className="justify-center pt-0">
                            <Button variant="default" className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-default" onClick={() => window.open('https://maps.google.com', '_blank')}>
                                View Map
                            </Button>
                        </CardFooter>
                    </Card>

                    <Card className="border-border bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-colors shadow-md">
                        <CardHeader className="text-center pb-2">
                            <div className="mx-auto bg-secondary p-4 rounded-xl mb-4">
                                <Mail className="size-12 text-primary" />
                            </div>
                            <CardTitle className="text-2xl font-default">Email</CardTitle>
                            <CardDescription className="text-base font-default">Business Collaboration & Investment Inquiry</CardDescription>
                        </CardHeader>
                        <CardContent className="text-center pb-6">
                            <p className="text-lg font-medium text-foreground truncate px-2 font-mono">{email}</p>
                        </CardContent>
                        <CardFooter className="justify-center pt-0">
                            <Button variant="outline" className="w-full border-primary text-primary hover:bg-primary/5 font-default" onClick={() => handleCopy(email, 'email')}>
                                {copiedEmail ? <Check className="mr-2 size-4" /> : <Copy className="mr-2 size-4" />}
                                {copiedEmail ? 'Copied' : 'Copy Email'}
                            </Button>
                        </CardFooter>
                    </Card>

                    <Card className="border-border bg-card/50 backdrop-blur-sm hover:border-primary/30 transition-colors shadow-md">
                        <CardHeader className="text-center pb-2">
                            <div className="mx-auto bg-secondary p-4 rounded-xl mb-4">
                                <MessageSquare className="size-12 text-primary" />
                            </div>
                            <CardTitle className="text-2xl font-default">Live Chat</CardTitle>
                            <CardDescription className="text-base font-default">Chat instantly with our investment advisors</CardDescription>
                        </CardHeader>
                        <CardContent className="text-center pb-6">
                            <p className="text-lg font-medium text-muted-foreground font-default">Average response time: &lt; 10 minutes</p>
                        </CardContent>
                        <CardFooter className="justify-center pt-0">
                            <Button variant="secondary" className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 font-default">
                                <MessageSquare className="mr-2 size-4" />
                                Start Chat
                            </Button>
                        </CardFooter>
                    </Card>
                </div>

                <div className="max-w-4xl mx-auto bg-card border border-border rounded-2xl p-6 md:p-10 shadow-2xl">
                    <div className="mb-8">
                        <h3 className="text-2xl font-bold font-default mb-2 text-primary">Investment Inquiry Form</h3>
                        <p className="text-muted-foreground font-default">Please fill out the information below; our professional team will provide preliminary strategic advice or project feedback within 24 hours.</p>
                    </div>
                    
                    <FormProvider formKey="contact_form_home" formId="contact_form_home" sectionName="Contact Us" defaultValues={{
          name: "",
          phone: "",
          email: "",
          company: "",
          interest: "",
          issue: "",
          preferred_contact: "Phone"
        }} validationRules={{
          name: {
            required: true
          },
          phone: {
            required: true
          },
          email: {
            required: true,
            pattern: "email"
          },
          company: {
            required: true
          },
          interest: {
            required: true
          },
          issue: {
            required: true
          },
          preferred_contact: {
            required: true
          }
        }} submitText="Submit Inquiry Now" successMessage="Your inquiry has been received. Our investment team will contact you as soon as possible.">
                        <SupportForm />
                    </FormProvider>
                </div>
            </div>
        </section>;
}